// ABOUTME: Shared ordered Q&A transcript renderer, extracted from EvidenceSourceModal
// ABOUTME: Used by the evidence modal (with source highlight) and the interview detail page (with evidence chips)

import { Link } from 'react-router-dom'
import { hypothesisCode, hypothesisPath } from '@/lib/hypothesisIds'

export interface TranscriptQAItem {
  question_number: number | null
  question_text: string
  answer_text: string
  /** Optional hypothesis chips shown under the answer (detail page) */
  evidence?: { evidence_id: string; hypothesis_id: string | null }[]
}

interface TranscriptViewProps {
  qa: TranscriptQAItem[]
  /** Highlight the Q&A that produced the evidence being traced (modal) */
  highlightQuestionNumber?: number | null
}

export function TranscriptView({ qa, highlightQuestionNumber }: TranscriptViewProps) {
  return (
    <div className="space-y-4">
      {qa.map((item, index) => {
        const isSourceQA =
          highlightQuestionNumber != null && item.question_number === highlightQuestionNumber
        return (
          <div
            key={index}
            className={`rounded-lg p-4 transition-all ${
              isSourceQA
                ? 'bg-amber-100 border-2 border-amber-400 shadow-md'
                : 'bg-gray-50 border border-gray-200'
            }`}
          >
            {isSourceQA && (
              <div className="mb-2 flex items-center gap-2 text-amber-700 font-semibold text-xs uppercase tracking-wide">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Evidence Source Q&A
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <span className={`font-bold text-sm flex-shrink-0 ${
                  isSourceQA ? 'text-amber-700' : 'text-gray-600'
                }`}>
                  Q{item.question_number || index + 1}:
                </span>
                <p className={`font-medium ${isSourceQA ? 'text-gray-900' : 'text-gray-700'}`}>
                  {item.question_text}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className={`font-bold text-sm flex-shrink-0 ${
                  isSourceQA ? 'text-amber-700' : 'text-gray-600'
                }`}>
                  A:
                </span>
                <p className={isSourceQA ? 'text-gray-800' : 'text-gray-600'}>
                  {item.answer_text}
                </p>
              </div>
              {item.evidence && item.evidence.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1 pl-7">
                  <span className="text-xs text-gray-500">
                    {item.evidence.length} evidence →
                  </span>
                  {[...new Set(item.evidence.map((e) => e.hypothesis_id).filter((h): h is string => !!h))].map((hid) => (
                    <Link
                      key={hid}
                      to={hypothesisPath(hid)}
                      className="text-xs px-2 py-0.5 rounded-full bg-alx-green/10 text-alx-blue hover:bg-alx-green/20 transition-colors"
                    >
                      {hypothesisCode(hid)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
